import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DealRegistrationResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useFormat, useT, useErrorMessage } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { RegistrationDecision } from '../setup/partners-panel.js';

/**
 * Which partner brought this deal in (F-061).
 *
 * Nothing at all for a deal sold directly - which is most of them, and a panel
 * saying "not from a partner" on every one is noise. Never the collision: a
 * reader of this deal may not be able to read the deal it collided with, so
 * that lives in Setup behind `partner.manage`.
 */
export function DealRegistrationPanel({ dealId }: { dealId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const entitlement = useEntitlement('F-061');

  const registration = useQuery<DealRegistrationResult, ApiError>({
    queryKey: ['deal-registration', dealId],
    queryFn: () => api.dealRegistration({ path: { dealId } }),
    enabled: entitlement.enabled,
  });

  if (!entitlement.enabled) return null;
  if (registration.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (registration.isError) {
    return (
      <Panel data-region="deal-registration">
        <p data-deal-registration-error className="text-dense text-danger-text">
          {errorText(registration.error)}
        </p>
      </Panel>
    );
  }
  const current = registration.data.registration;
  if (!current) return null;

  return (
    <Panel data-region="deal-registration" data-registration-status={current.status}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('partner.dealPanel')}</SectionLabel>
          <span className="rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
            {t(`partner.status.${current.status}` as 'partner.status.pending')}
          </span>
        </div>
        <p className="text-body text-ink">
          {t('partner.registeredBy', {
            values: {
              person: current.registeredBy ?? t('partner.someone'),
              partner: current.partner,
              date: formatDate(current.registeredAt, format),
            },
          })}
        </p>
        {current.protectedUntil ? (
          <p className="text-meta text-ink-meta">
            {t('partner.protectedUntil', { values: { date: formatDate(current.protectedUntil, format) } })}
          </p>
        ) : null}
        {current.status === 'rejected' && current.note ? (
          <p data-registration-reason className="text-meta text-ink-meta">
            {t('partner.toldPartner', { values: { note: current.note } })}
          </p>
        ) : null}
        {current.status === 'pending' && can('partner.manage') ? (
          <RegistrationDecision
            dealId={dealId}
            onDecided={() =>
              Promise.all([
                queryClient.invalidateQueries({ queryKey: ['deal-registration', dealId] }),
                queryClient.invalidateQueries({ queryKey: ['partners'] }),
              ])
            }
          />
        ) : null}
      </div>
    </Panel>
  );
}
